import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Folder } from 'lucide-react';

import { useForumNodes } from '@/hooks/useForum';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  childrenOf,
  forumStats,
  nodeType,
  timeAgo,
  type ForumNode,
  type ForumTree,
} from '@/lib/forum';
import { ForumHeader } from './ForumHeader';
import { Breadcrumbs } from './Breadcrumbs';
import { NewCategoryDialog } from './NewCategoryDialog';
import { NewForumDialog } from './NewForumDialog';
import { EditNodeDialog } from './EditNodeDialog';
import { ForumFooter } from './ForumFooter';
import { AuthorName } from './Author';
import { Skeleton } from '@/components/ui/skeleton';

export function ForumIndex() {
  useSeoMeta({
    title: 'NostrBoard — Nostr Forum',
    description: 'A decentralized discussion forum built on Nostr (kind 32323).',
  });

  const { data, isLoading } = useForumNodes();
  const { user } = useCurrentUser();

  const categories: ForumNode[] = data
    ? data.nodes
        .filter((n) => nodeType(n, data.byId) === 'category')
        .sort((a, b) => a.createdAt - b.createdAt)
    : [];

  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-5">
        <Breadcrumbs items={[{ label: 'Board index' }]} />

        {user && (
          <div className="mb-4 flex justify-end">
            <NewCategoryDialog />
          </div>
        )}

        {isLoading ? (
          <IndexSkeleton />
        ) : categories.length === 0 ? (
          <EmptyBoard loggedIn={Boolean(user)} />
        ) : (
          categories.map((cat) => (
            <CategorySection key={cat.id} category={cat} tree={data!} />
          ))
        )}

        <ForumFooter />
      </main>
    </div>
  );
}

function CategorySection({ category, tree }: { category: ForumNode; tree: ForumTree }) {
  const { user } = useCurrentUser();
  const forums = childrenOf(tree.byId, category.id).sort((a, b) => a.createdAt - b.createdAt);

  return (
    <section className="mb-5">
      <div className="flex items-center justify-between rounded-t-[6px] border border-[#0d4f74] bg-[linear-gradient(to_bottom,#2a7fb8,#105B86)] px-4 py-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-white">
          {category.title || 'Untitled category'}
        </h2>
        {user && (
          <div className="flex items-center gap-2">
            <EditNodeDialog node={category} />
            <NewForumDialog categoryId={category.id} />
          </div>
        )}
      </div>
      <div className="divide-y divide-[#ececec] rounded-b-[6px] border border-t-0 border-[#d6d6d6] bg-white">
        {forums.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[#777]">No forums yet.</div>
        ) : (
          forums.map((forum) => <ForumRow key={forum.id} forum={forum} tree={tree} />)
        )}
      </div>
    </section>
  );
}

function ForumRow({ forum, tree }: { forum: ForumNode; tree: ForumTree }) {
  const stats = forumStats(tree.byId, forum.id);
  const last = stats.lastPost;

  return (
    <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[#f6f9fc]">
      <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded bg-[#eaf2f9] text-[#105B86] sm:flex">
        <Folder className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <Link
          to={`/f/${forum.id}`}
          className="text-[15px] font-bold text-[#105B86] hover:underline"
        >
          {forum.title || 'Untitled forum'}
        </Link>
        {forum.content && (
          <p className="mt-0.5 line-clamp-2 text-sm text-[#666]">{forum.content}</p>
        )}
      </div>
      <div className="hidden w-24 shrink-0 text-center text-xs text-[#666] md:block">
        <div>
          <span className="font-semibold text-[#333]">{stats.topics}</span> topics
        </div>
        <div>
          <span className="font-semibold text-[#333]">{stats.posts}</span> posts
        </div>
      </div>
      <div className="hidden w-44 shrink-0 text-xs text-[#666] lg:block">
        {last ? (
          <div className="truncate">
            <span>Last post by </span>
            <AuthorName pubkey={last.pubkey} className="font-normal" />
            <div className="text-[#999]">{timeAgo(last.createdAt)}</div>
          </div>
        ) : (
          <span>No posts</span>
        )}
      </div>
      <EditNodeDialog node={forum} />
    </div>
  );
}

function EmptyBoard({ loggedIn }: { loggedIn: boolean }) {
  return (
    <div className="rounded-[6px] border border-dashed border-[#c9c9c9] bg-white px-8 py-12 text-center">
      <p className="text-[#666]">
        No categories yet.{' '}
        {loggedIn
          ? 'Click “New category” above to get started.'
          : 'Log in and create the first category to get things rolling.'}
      </p>
    </div>
  );
}

function IndexSkeleton() {
  return (
    <div className="space-y-5">
      {[0, 1].map((s) => (
        <section key={s} className="mb-5">
          <Skeleton className="h-9 w-full rounded-t-[6px]" />
          <div className="divide-y divide-[#ececec] rounded-b-[6px] border border-t-0 border-[#d6d6d6] bg-white">
            {[0, 1, 2].map((r) => (
              <div key={r} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-10 w-10 rounded" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-72" />
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
